<template>
  <div class="d-flex fd-column bg-white x-standard-shadow">
    <div class="bd-b-gray30 p-y-3">
      <div class="layout p-x-3">
        <div class="layout-item">
          <div class="d-flex ai-center">
            <div class="h5 text-bold d-iblock m-r-1" selenium="job-subtask-label">
              {{ workOrder.service_display_name }}
            </div>
            <job-status-badge
                :job-id="jobId"
                class="d-none:mo"
                selenium="lead-status-badge"/>
          </div>
          <div
              class="c-gray50 m-b-1"
              selenium="job-submission-time-label">
            requested on {{ workOrder.time_created | formatDate('M/d/yyyy') }}
          </div>

          <div class="d-flex">
            <div class="checkboxharmony">
              <input
                  v-model="isChecked"
                  tabindex="-1"
                  :id="'check'+jobId"
                  type="checkbox"
                  @change="updateSmsOptIn"/>
              <label :for="'check'+jobId">
                <span class="p-b-1:mo">{{ optInText }}</span>
              </label>
            </div>
            <div
                class="d-flex ac-center INFO cur-pointer p-l-1 p-l-0:mo"
                @click="smsModal=true">
              <vk-icon
                  name="exclamation-circle-outlined"
                  size="18"
              />
            </div>
          </div>

          <div class="d-flex jc-between d-none:t">
            <job-status-badge :job-id="jobId" class="m-b-1" />
            <service-actions
                :job-id="jobId"
                class="d-flex"
                @showCancelationModal="isConfirmationModalActive = true"/>
          </div>
        </div>
        <div class="layout-item flex-0-0">
          <service-actions
              :job-id="jobId"
              class="h-100 d-flex ai-center:t d-none:mo"
              @showCancelationModal="isConfirmationModalActive = true"/>
        </div>
      </div>
    </div>
    <div class="p-y-2 p-x-3" selenium="job-canceled-expired-label">
      <template v-if="shouldShowExpiredMessage">
        We’re sorry, this job is no longer active because
        the Pros we matched you with were not available.
      </template>
      <template v-else-if="shouldShowCanceledMessage">
        This job was canceled.
      </template>
      <template v-else>
        <div class="text-bold m-b-2" selenium="who-are-pros-desc-label">
          We've matched you with these local independent Pros:
        </div>
        <div class="layout gap-3">
          <div
              v-for="match in activeMatches"
              :key="match.id"
              class="layout-item is-6:t is-4:w"
              selenium="pro-cards-container">
            <pro-card
                :service-display-name="workOrder.service_display_name"
                :match="match"
                :has-review="!!tempReviewObj[match.service_provider_user_identifier]"
                :has-unread-message="hasUnreadMessage(match)"
                @leaveFeedback="openReviewModal"
                @sendMessage="goToMessages(match)"
                @updateTopPro="showTopPro=true"
            />
          </div>
        </div>
      </template>
      <div class="link p-y-2" selenium="see-all-matched-pros-link" @click="showModal = true">
        See all matched Pros
        <portal v-if="showModal" to="MODAL">
          <all-matched-pros-modal
              :get-is-unreachable-match="getIsUnreachableMatch"
              :get-is-active-match="getIsActiveMatch"
              :matches="archivedOrCanceledMatches"
              @close="showModal = false"
              @match-clicked="goToMessages"
          />
        </portal>
      </div>
    </div>
    <div class="bd-t-gray30 w-100">
      <credential-guide :showTopPro="showTopPro" class="p-3"/>
    </div>
    <cancel-request-confirmation-modal
        v-if="isConfirmationModalActive"
        :canceling="canceling"
        @close="closeConfirmationModal"
        @cancelRequest="cancelRequest"/>

    <review-modal
        v-if="activeReviewModal"
        :modal-data="reviewModalData"
        @next="next"/>

    <sms-modal
        :isOpen="smsModal"
        @close="closeSMSModal"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import http from '@homeservices/api-kit/axios';

import { getReview } from '@homeservices/api-kit/workorder/reviews';
import workOrder from '@/mixins/work-order';
import publicProfileMixin from '@/mixins/public-profile';
import { NEW_MATCH_STATUSES } from '@/constants/match-statuses';
import ReviewModal from '@/views/Consumer/components/ReviewModal';
import CredentialGuide from '@/views/JobRequest/Matches/components/ProSelection/components/CredentialGuide';
import AllMatchedProsModal from './components/AllMatchedProsModal';
import CancelRequestConfirmationModal from './components/CancelRequestConfirmationModal';
import ServiceActions from './components/ServiceActions';
import SMSModal from './components/SMSModal';
import ProCard from '../ProCard';
import JobStatusBadge from './components/JobStatusBadge';

const UNREACHABLE_MATCH_STATUSES = [
  NEW_MATCH_STATUSES.REJECTED,
  NEW_MATCH_STATUSES.UNRESPONSIVE,
  NEW_MATCH_STATUSES.CLOSED,
  NEW_MATCH_STATUSES.CANCELED,
];

const ACTIVE_PRO_MATCH_STATUSES = [
  NEW_MATCH_STATUSES.UNREPLIED,
  NEW_MATCH_STATUSES.MATCHED,
  NEW_MATCH_STATUSES.ACCEPTED,
  NEW_MATCH_STATUSES.POTENTIAL,
  NEW_MATCH_STATUSES.HIRED,
  NEW_MATCH_STATUSES.COMPLETED,
];

const ARCHIVED_OR_CANCELED_MATCH_STATUSES = [
  NEW_MATCH_STATUSES.CLOSED,
  NEW_MATCH_STATUSES.CANCELED,
  NEW_MATCH_STATUSES.REJECTED,
  NEW_MATCH_STATUSES.UNREPLIED,
  NEW_MATCH_STATUSES.MATCHED,
  NEW_MATCH_STATUSES.ACCEPTED,
  NEW_MATCH_STATUSES.POTENTIAL,
  NEW_MATCH_STATUSES.HIRED,
  NEW_MATCH_STATUSES.COMPLETED,
];

const cancelSourcesSet = new Set();

export default {
  MATCH_STATUSES: NEW_MATCH_STATUSES,
  name: 'service-card',
  components: {
    AllMatchedProsModal,
    CancelRequestConfirmationModal,
    ServiceActions,
    ProCard,
    JobStatusBadge,
    ReviewModal,
    CredentialGuide,
    'sms-modal': SMSModal,
  },
  mixins: [
    workOrder,
    publicProfileMixin,
  ],
  props: {
    // Used by workOrder mixin
    jobId: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      isConfirmationModalActive: false,
      canceling: false,
      showModal: false,
      activeReviewModal: '',
      tempReviewObj: {},
      reviewModalData: {},
      showTopPro: false,
      isChecked: false,
      infoContent: 'Pro Referral may send you text messages about this service request. Message & data rates may apply. Texts message frequency varies and may be sent between 8:00 am & 9:00 pm. Text STOP to cancel or HELP for help. For questions, contact 1-855-723-2266.',
      smsModal: false,

    };
  },
  computed: {
    ...mapGetters('consumer/jobReviews', ['getReview']),
    ...mapGetters('account/pro/pricing', [
      'pricingList',
    ]),
    activeMatchStatuses () {
      return ACTIVE_PRO_MATCH_STATUSES;
    },
    activeMatches () {
      return this.workOrder.matches.filter(match => this.getIsActiveMatch(match));
    },
    archivedOrCanceledMatches () {
      return this.workOrder.matches
        .filter(match => ARCHIVED_OR_CANCELED_MATCH_STATUSES.includes(match.match_status))
        .slice()
        .reverse();
    },
    shouldShowExpiredMessage () {
      return this.workOrder.isExpired && this.activeMatches.length === 0;
    },
    shouldShowCanceledMessage () {
      return (
        (this.workOrder.isCanceled || this.workOrder.isCancelled)
        && this.activeMatches.length === 0
      );
    },
    optInText () {
      return this.isChecked ? 'You are opted in for text messages. Uncheck to stop receiving text messages.' : 'Get text message updates about your service request';
    },
  },
  watch: {
    activeMatches () {
      getReview({ order_hashes: this.workOrder.order_hash, from: 0 })
        .then((res) => {
          this.setReviews(res.data);
        });
    },
    'workOrder.sms_opt_in': {
      handler: function Handler (newValue) {
        this.isChecked = newValue;
      },
      immediate: true,
    },

  },
  destroyed () {
    cancelSourcesSet.forEach((source) => { source.cancel(); });
    cancelSourcesSet.clear();
  },
  methods: {
    setReviews (reviews) {
      if (reviews.count) {
        const skillProfiles = this.activeMatches.map(m => (m.service_provider_user_identifier));
        const matchReviews = skillProfiles.reduce((acc, cur) => {
          // Get service_provider_id using kill_profile
          const providerId = this.getPublicProfileByServiceProviderIdentifier(
            cur
          ).service_provider.id;
          // Find a review in which service_provider_id match the profile providerId
          const review = reviews.results
            .filter(r => (r.service_provider_id === providerId));
          // Match Skill profile as KEY and review as VALUE
          acc[cur] = review.length ? review[0] : null;
          return acc;
        }, {});

        this.tempReviewObj = matchReviews;
      }
    },
    getIsPublicProfileActive (match) {
      const publicProfile = this.getPublicProfileByServiceProviderIdentifier(
        match.service_provider_user_identifier
      );
      return publicProfile && publicProfile.service_provider.eligible_for_public_profile;
    },
    getIsUnreachableMatch (match) {
      return (
        UNREACHABLE_MATCH_STATUSES.includes(match.match_status)
        || !this.getIsPublicProfileActive(match)
      );
    },
    getIsActiveMatch (match) {
      return this.activeMatchStatuses.includes(match.match_status)
        && this.getIsPublicProfileActive(match);
    },
    hasUnreadMessage (match) {
      return this.$store.getters['consumer/inbox/hasUnreadMessage'](
        this.jobId,
        match.service_provider_user_identifier
      );
    },
    goToMessages (match) {
      // if match is not loaded, don't redirect.
      if (!match) { return; }

      if (!this.getIsUnreachableMatch(match)) {
        const params = {
          jobId: this.jobId,
          skillsProfileId: match.service_provider_id,
        };

        this.$store.dispatch('consumer/inbox/markAsRead', {
          jobId: this.jobId,
          proUserId: match.service_provider_user_identifier,
        });

        this.$events.$emit(this.$events.CONSUMER_VIEWED_PRO_CARD, params);
        this.$router.push({ name: 'consumer::messages', params });
      }
    },
    showConfirmationModal () {
      this.isConfirmationModalActive = true;
    },
    closeConfirmationModal () {
      this.isConfirmationModalActive = false;
    },
    async cancelRequest (payload) {
      this.canceling = true;
      await this.$store.dispatch('consumer/jobs/cancelJobConsumer', {
        id: this.workOrder.order_hash,
        jobId: this.jobId,
        reason: payload,
      });
      this.closeConfirmationModal();
      this.canceling = false;
    },
    async openReviewModal ({ skillsProfileId }) {
      this.reviewModalData.skillsProfileId = skillsProfileId;

      // This is an interesting logic, if we have a review, just show the review
      // if not, either we are still fetching the review or we've fetched it and
      // it doesn't have it but to make sure we have to do the api call to
      // show correct modal
      // TODO: Fix this logic once review api gets overhauled
      if (this.tempReviewObj[skillsProfileId]) {
        const publicProfile = this.getPublicProfileByServiceProviderIdentifier(skillsProfileId);
        this.reviewModalData = {
          ...this.reviewModalData,
          ...this.tempReviewObj[skillsProfileId],
          customer: this.workOrder.user,
          proName: publicProfile && publicProfile.service_provider.business_name,
        };
        this.activeReviewModal = 'view';
      } else {
        this.$router.push({
          name: 'consumer::reviews::new',
          query: {
            job_id: this.jobId,
            profile_id: skillsProfileId,
            skills_profiles: this.workOrder.matches.map(p => (p.service_provider_user_identifier)),
          },
        });
      }
    },
    next (payload) {
      if (payload) {
        if (payload.modal) {
          this.activeReviewModal = payload.modal;
        } else if (payload.submitted) {
          this.activeReviewModal = 'acknowledge';
          setTimeout(() => {
            this.activeReviewModal = '';
          }, 2000);
        }
      } else {
        this.activeReviewModal = '';
      }
    },
    async updateSmsOptIn () {
      try {
        if (this.isChecked) {
          await http.put(`/platform/workorder/r/workorder/sms/optin/v2?work_order_hash=${this.workOrder.order_hash}`);
        } else {
          await http.put(`/platform/workorder/r/workorder/sms/optout/v1?work_order_hash=${this.workOrder.order_hash}`);
        }
      } catch (error) {
        console.error(error);
      }
    },
    closeSMSModal () {
      this.smsModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools.scss";
  .ICON-WRAPPER {
    min-width: 80px;
  }

  .JOB-ICON {
    @include square(50px);
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $color-depot;
    border-radius: 50%;
  }
  .INFO {
    color: #1890FF;
    align-self: center;
  }
</style>
